import ControlPointIcon from '@mui/icons-material/ControlPoint';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Chip, Menu, MenuItem } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import {
    Identifier,
    useGetList,
    useGetMany,
    useRecordContext,
    useUpdate,
} from 'react-admin';

import { TagChip } from '../scmtags/TagChip';
import { TagCreateModal } from '../scmtags/TagCreateModal';
import { Contact, ScmTag } from '../types';

export const TagsListEdit = () => {
    const record = useRecordContext<Contact>();
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const { data: allTags, isPending: isPendingAllTags } = useGetList<ScmTag>(
        'scmtags',
        {
            pagination: { page: 1, perPage: 10 },
            sort: { field: 'descricao_tag', order: 'ASC' },
            filter: { tipo: "fornecedor" }
        }
    );
    const { data: tags, isPending: isPendingRecordTags } = useGetMany<ScmTag>(
        'scmtags',
        { ids: record?.ids_tags },
        { enabled: record && record.ids_tags && record.ids_tags.length > 0 }
    );
    const [update] = useUpdate<Contact>();

    const unselectedTags =
        allTags &&
        record &&
        record.ids_tags &&
        allTags.filter(tag => !record.ids_tags.includes(tag.id));

    const handleMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleTagAdd = (id: Identifier) => {
        if (!record) {
            throw new Error('No contact record found');
        }
        const tags = [...record.ids_tags, id];
        update('suppliercontacts', {
            id: record.id,
            data: { ...record, ids_tags: tags },
            previousData: record,
        });
        setAnchorEl(null);
    };

    const handleTagDelete = async (id: Identifier) => {
        if (!record) {
            throw new Error('No contact record found');
        }
        const tags = record.ids_tags.filter(tagId => tagId !== id);
        await update('suppliercontacts', {
            id: record.id,
            data: { ...record, ids_tags: tags },
            previousData: record,
        });
    };

    const openTagCreateDialog = () => {
        setOpen(true);
        setAnchorEl(null);
    };

    const handleTagCreateClose = () => {
        setOpen(false);
    };

    const handleTagCreated = React.useCallback(
        async (tag: ScmTag) => {
            if (!record) {
                throw new Error('No contact record found');
            }

            await update('suppliercontacts',
                {
                    id: record.id,
                    data: { ...record, ids_tags: [...record.ids_tags, tag.id] },
                    previousData: record,
                },
                {
                    onSuccess: () => {
                        setOpen(false);
                    },
                }
            );
        },
        [update, record]
    );

    if (isPendingRecordTags || isPendingAllTags) return null;
    return (
        <>
            {tags?.map(tag => (
                <Box mt={1} mb={1} key={tag.id}>
                    <TagChip
                        tag={tag}
                        onUnlink={() => handleTagDelete(tag.id)}
                        key={tag.id}
                    />
                </Box>
            ))}
            <Box mt={1}>
                <Chip
                    icon={<ControlPointIcon />}
                    size="small"
                    variant="outlined"
                    onClick={handleMenuOpen}
                    label="Adicionar tag"
                    color="primary"
                />
            </Box>
            <Menu
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorEl={anchorEl}
            >
                {unselectedTags?.map(tag => (
                    <MenuItem key={tag.id} onClick={() => handleTagAdd(tag.id)}>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={tag.descricao_tag}
                            style={{
                                backgroundColor: tag.color,
                                border: 0,
                            }}
                            onClick={() => handleTagAdd(tag.id)}
                        />
                    </MenuItem>
                ))}
                <MenuItem onClick={openTagCreateDialog}>
                    <Chip
                        icon={<EditIcon />}
                        size="small"
                        variant="outlined"
                        onClick={openTagCreateDialog}
                        color="primary"
                        label="Criar nova tag"
                    />
                </MenuItem>
            </Menu>
            <TagCreateModal
                tipo="fornecedor"
                open={open}
                onClose={handleTagCreateClose}
                onSuccess={handleTagCreated}
            />
        </>
    );
};
